import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Wind, Heart, Brain, Sparkles, ArrowRight, BookOpen, Feather, BookMarked, Tablet } from "lucide-react";
import { LOGO_URL, DIMENSIONS, RITUAL, CARNET_ACCENTS } from "@/data/constants";
import { useBookLinks } from "@/lib/useBookLinks";
import { DimensionCard } from "@/components/DimensionCard";
import { FoundationFeature } from "@/components/FoundationFeature";
import { HomeQuiz } from "@/components/HomeQuiz";
import { PackHighlight } from "@/components/PackHighlight";
import { CoffretHighlight } from "@/components/CoffretHighlight";

const ritualIcons = [Wind, Heart, Sparkles, Brain];

export default function Home() {
  const bookLinks = useBookLinks();
  const [books, setBooks] = useState([]);
  useEffect(() => {
    api
      .get("/books")
      .then((r) => setBooks(r.data.filter((b) => b.number >= 1)))
      .catch(() => {});
  }, []);
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 pb-20 md:pt-20 md:pb-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="rise">
            <p className="font-sans text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A24B] font-semibold mb-4">
              Corps • Cœur • Cerveau • Conscience
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#1F2A24]">
              Un chemin vers une vie plus pleine
            </h1>
            <p className="font-sans text-base md:text-lg text-[#1F2A24]/80 mt-6 max-w-xl leading-relaxed">
              Une méthode pour explorer quatre dimensions de soi — Corps, Cœur, Cerveau, Conscience —
              et transformer l'expérience en chemin personnel.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/jeu"
                data-testid="cta-begin-free"
                className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[#2c4a2e] transition-colors"
              >
                COMMENCER GRATUITEMENT
                <ArrowRight size={18} />
              </Link>
              <a
                href="#les-4c"
                data-testid="cta-discover-4c"
                className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[#C9A24B]/10 transition-colors"
              >
                DÉCOUVRIR LES 4 C
              </a>
            </div>
            <p className="font-sans text-xs tracking-[0.22em] text-[#1F2A24]/50 mt-8">
              ÉQUILIBRER • RAYONNER • CRÉER • ÊTRE
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#C9A24B]/10 blur-2xl scale-110" />
              <img
                src={LOGO_URL}
                alt="Logo Le Tao des 4C"
                data-testid="hero-logo"
                className="relative w-72 h-72 md:w-[26rem] md:h-[26rem] object-contain drop-shadow-sm"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <FoundationFeature />

      {/* TROIS EXPÉRIENCES */}
      <section className="bg-[#1F2A24] text-[#F6F2E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24">
          <div className="text-center">
            <p className="font-sans text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">Un univers, trois façons de le vivre</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-2">Trois expériences complémentaires</h2>
            <div className="gold-divider w-24 mt-4 mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12" data-testid="home-experiences">
            {[
              { key: "aventure", icon: BookOpen, name: "L'Aventure", tagline: "Je vis l'histoire.", desc: "Une aventure interactive en 21 étapes, avec choix, personnages, objets et plusieurs chemins.", to: "/univers/ile-de-labondance", cta: "Vivre l'histoire" },
              { key: "jeu", icon: Sparkles, name: "Le Jeu", tagline: "Je joue et j'explore.", desc: "21 cartes interactives pour questionner son quotidien, répondre et passer à l'action.", to: "/jeu/ile", cta: "Jouer et explorer" },
              { key: "carnets", icon: Feather, name: "Les Carnets", tagline: "Je réfléchis et j'approfondis.", desc: "Des parcours de 21 jours pour approfondir les thèmes de chaque univers.", to: "/carnets", cta: "Approfondir" },
            ].map((e) => {
              const Icon = e.icon;
              return (
                <Link key={e.key} to={e.to} data-testid={`home-exp-${e.key}`} className="group rounded-2xl border border-[#C9A24B]/25 bg-white/5 p-7 flex flex-col hover:border-[#C9A24B]/60 transition-colors">
                  <span className="w-11 h-11 rounded-full bg-[#C9A24B]/15 text-[#C9A24B] flex items-center justify-center"><Icon size={20} /></span>
                  <h3 className="font-serif text-2xl mt-5">{e.name}</h3>
                  <p className="font-serif italic text-[#C9A24B] mt-1">{e.tagline}</p>
                  <p className="font-sans text-sm text-[#F6F2E9]/70 mt-3 flex-1 leading-relaxed">{e.desc}</p>
                  <span className="inline-flex items-center gap-2 font-ui text-sm text-[#F6F2E9] mt-5 group-hover:gap-3 transition-all">{e.cta} <ArrowRight size={16} className="text-[#C9A24B]" /></span>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/univers" data-testid="home-explore-univers" className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[#b8923f] transition-colors">
              EXPLORER LES UNIVERS <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <PackHighlight />
      <CoffretHighlight />

      <HomeQuiz />

      {/* LE LIVRE */}
      <section className="bg-[#1F2A24] text-[#F6F2E9]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 md:py-20">
          <div className="rounded-3xl border border-[#C9A24B]/30 bg-white/5 p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] font-semibold">Le livre</p>
              <h2 className="font-serif text-3xl md:text-4xl mt-2">Le Tao des 4C</h2>
              <p className="font-sans text-sm text-[#F6F2E9]/70 mt-3 max-w-xl leading-relaxed">
                Retrouvez la méthode de Denis Tran en version imprimée ou numérique, disponible sur Amazon.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a href={bookLinks.print} target="_blank" rel="noopener noreferrer" data-testid="book-print-link"
                 className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[#b8923f] transition-colors">
                <BookMarked size={18} /> Livre broché
              </a>
              <a href={bookLinks.ebook} target="_blank" rel="noopener noreferrer" data-testid="book-ebook-link"
                 className="inline-flex items-center justify-center gap-2 rounded-full border border-[#C9A24B]/50 text-[#F6F2E9] px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-white/10 transition-colors">
                <Tablet size={18} /> Version Kindle
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* LES 4 C */}
      <section id="les-4c" className="bg-[#F8F5EE] border-y border-[#E8DCC4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24">
          <SectionHead eyebrow="La méthode" title="Les 4 C" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {DIMENSIONS.map((d) => (
              <DimensionCard key={d.key} dim={d} testid={`home-card-${d.key}`} />
            ))}
          </div>
        </div>
      </section>

      {/* UNE PRATIQUE SIMPLE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24">
        <SectionHead eyebrow="Le rituel des 4 C" title="Une pratique simple" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10">
          {RITUAL.map((step, i) => {
            const Icon = ritualIcons[i];
            return (
              <div
                key={step}
                data-testid={`ritual-step-${i}`}
                className="rounded-2xl bg-white border border-[#E8DCC4] p-6 text-center"
              >
                <div className="w-11 h-11 mx-auto rounded-full bg-[#C9A24B]/15 text-[#C9A24B] flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <p className="font-sans text-xs text-[#1F2A24]/40 mt-4">0{i + 1}</p>
                <p className="font-serif text-lg text-[#1F2A24] mt-1">{step}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CARNETS */}
      <section className="bg-[#1F2A24] text-[#F6F2E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold mb-4">
              La collection
            </p>
            <h2 className="font-serif text-3xl md:text-4xl">Les Carnets 21 Jours</h2>
            <p className="font-serif italic text-lg text-[#F6F2E9]/80 mt-4 max-w-lg">
              « 21 jours pour expérimenter la méthode dans votre vie quotidienne. »
            </p>
            <Link
              to="/carnets"
              data-testid="cta-discover-carnets"
              className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide mt-8 hover:bg-[#b8923f] transition-colors"
            >
              DÉCOUVRIR LES CARNETS
              <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3" data-testid="home-carnets-grid">
            {books.slice(0, 6).map((b, i) => {
              const accent = CARNET_ACCENTS[i % CARNET_ACCENTS.length];
              return (
                <Link
                  key={b.id}
                  to="/carnets"
                  data-testid={`home-carnet-${b.number}`}
                  className="group aspect-[3/4] rounded-lg overflow-hidden relative border border-white/10"
                >
                  <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${accent}, ${accent}cc)` }} />
                  {b.cover && (
                    <img
                      src={b.cover}
                      alt={b.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(to top, rgba(31,42,36,0.82), rgba(31,42,36,0.10))" }}
                  />
                  <div className="relative h-full flex flex-col justify-between p-3">
                    <span className="font-sans text-[10px] tracking-[0.18em] text-white/85">
                      CARNET {String(b.number).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-xs text-white leading-tight drop-shadow-md line-clamp-2">
                      {b.title}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* LE JEU */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24 text-center">
        <SectionHead eyebrow="Expérimenter" title="Le Jeu" center />
        <p className="font-serif italic text-xl text-[#1F2A24]/70 mt-4">
          « Une carte. Une question. Une expérience. »
        </p>
        <Link
          to="/jeu"
          data-testid="cta-play-free"
          className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-7 py-3.5 font-ui text-sm tracking-wide mt-8 hover:bg-[#2c4a2e] transition-colors"
        >
          JOUER GRATUITEMENT
          <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}

export const SectionHead = ({ eyebrow, title, center }) => (
  <div className={center ? "text-center" : ""}>
    <p className="font-sans text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">
      {eyebrow}
    </p>
    <h2 className="font-serif text-3xl md:text-4xl text-[#1F2A24] mt-2">{title}</h2>
    <div className={`gold-divider w-24 mt-4 ${center ? "mx-auto" : ""}`} />
  </div>
);
